/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { heroBannerQuery } from '../../../modules/graphql/index';
import { cacheURL, cacheCNURL } from '../../../modules/utils/constants/constants';
import { HeroBannerImageProps, heroBannerImageType, RenditionsProps } from '../HeroBanner.types';
import { imageQueryParam } from '../../LocalServices/LocalServices.types';
import { PageParamsContext } from '../../../modules/context/PageContext';
import { isCNLocale } from '@marriott/shared/mi-helper-utils';

export const HeroBannerImage: FC<HeroBannerImageProps> = ({
  isAuthorMode,
  onHeroBannerImageDataFetched,
  heroBannerPageProps,
  heroBannerImage = null,
}) => {
  const { preProcessorResponse, sessionID, requestId, lang } = useContext(PageParamsContext);
  const isServer = !(typeof window != 'undefined' && window?.document);
  const baseUrl = isCNLocale(preProcessorResponse?.locale ?? 'en-US') ? cacheCNURL : cacheURL;
  const { data, loading, error } = useQuery(heroBannerQuery, {
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: preProcessorResponse?.marsha ?? '',
    },
    context: {
      headers: {
        'accept-language': lang ?? 'en-US',
        'x-b3-traceId': sessionID ?? 'fallback-token',
        'x-b3-spanId': requestId !== '' ? requestId : `${Date.now()}`,
        'correlation-id': sessionID ?? 'fallback-token',
      },
    },
    skip: isAuthorMode || !!heroBannerImage,
  });

  useEffect(() => {
    let heroImage = null;
    if (!loading) {
      if (data && !error) {
        const imagedata = data?.property?.media?.internetPortalPrimaryImage;
        if (imagedata && imagedata.length > 0 && imagedata?.[0]?.imageUrls?.classicHorizontal) {
          const dacImageQueryParamsHeroBanner: Array<imageQueryParam> =
            heroBannerPageProps?.imageQueryParamsHeroBanner ?? [];
          const wideHorizontal = imagedata?.[0]?.imageUrls?.wideHorizontal;
          const classicHorizontal = imagedata?.[0]?.imageUrls?.classicHorizontal;
          const square = imagedata?.[0]?.imageUrls?.square;

          heroImage = {
            altText: imagedata?.[0]?.alternateDescription,
            assetPath: imagedata?.[0]?.imageSrc,
            dynamic: false,
            renditions: dacImageQueryParamsHeroBanner.map(imageQueryParams => {
              const viewportQuery = imageQueryParams?.viewPortName?.split(':')[0];
              const viewportValue = imageQueryParams?.viewPortName?.split(':')[1];
              const mediaVal = Number(viewportValue.slice(0, imageQueryParams?.viewPortName?.split(':')[1].length - 2));
              const imagePath =
                mediaVal >= 992
                  ? wideHorizontal
                  : mediaVal === 576 && viewportQuery === 'min-width'
                  ? classicHorizontal
                  : mediaVal === 576 && viewportQuery === 'max-width'
                  ? square
                  : '';
              return {
                mediaQuery: viewportQuery,
                mediaValue: viewportValue,
                dynamic: false,
                renditionPath: `${baseUrl}${imagePath}?${imageQueryParams?.nonRetinaDisplayParams}, ${cacheURL}${imagePath}?${imageQueryParams?.retinaDisplayParams}`,
              };
            }),
          };
        }
      }

      if (!heroImage || error) {
        if (
          heroBannerPageProps?.authoredImage &&
          heroBannerPageProps?.authoredImage?.renditions &&
          heroBannerPageProps?.authoredImage?.renditions?.length > 0
        ) {
          heroImage = { ...heroBannerPageProps?.authoredImage };
        } else if (
          heroBannerPageProps?.fallbackBrandImage &&
          heroBannerPageProps?.fallbackBrandImage?.renditions &&
          heroBannerPageProps?.fallbackBrandImage?.renditions?.length > 0
        ) {
          heroImage = { ...heroBannerPageProps?.fallbackBrandImage };
        } else if (
          heroBannerPageProps?.dynamicImageFileReference &&
          heroBannerPageProps?.dynamicImageFileReference?.renditions &&
          heroBannerPageProps?.dynamicImageFileReference?.renditions?.length > 0
        ) {
          heroImage = { ...heroBannerPageProps?.dynamicImageFileReference };
        }
      }

      if (heroImage && heroImage?.assetPath && heroImage?.assetPath?.includes('/is/')) {
        const imageQueryParamsHeroBanner: Array<imageQueryParam> =
          heroBannerPageProps?.imageQueryParamsHeroBanner ?? [];
        const imageData: heroBannerImageType = { ...heroImage };
        const renditions = imageData?.renditions;
        const newRenditions: Array<RenditionsProps> | null = [];
        imageQueryParamsHeroBanner?.forEach(imageQueryParamHeroBanner => {
          let queryValue = imageQueryParamHeroBanner?.viewPortName?.split(': ')[1] ?? '';
          if (imageQueryParamHeroBanner?.viewPortName?.split(':')[0] === 'min-width' && queryValue === '576px') {
            queryValue = '577px';
          }
          let matchMedia = renditions?.find(rend => rend?.mediaValue === queryValue);

          if (matchMedia) {
            matchMedia = {
              ...matchMedia,
              renditionPath: `${matchMedia?.renditionPath}?${imageQueryParamHeroBanner?.nonRetinaDisplayParams}, ${matchMedia?.renditionPath}?${imageQueryParamHeroBanner?.retinaDisplayParams}`,
            };
            newRenditions.push(matchMedia);
          }
        });
        heroImage = { ...imageData, renditions: newRenditions };
      }
      onHeroBannerImageDataFetched(heroImage);
    }
  }, [heroBannerPageProps, loading, onHeroBannerImageDataFetched, data, error]);

  return null;
};
